// GAVION booking: the public page #/demo/:slug (spec §2). No login, no CRM chrome. FR by default, EN with ?lang=en.
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { BookingForm, EMPTY_FORM, type FieldErrors, type FormValues, validateBookingForm } from "./BookingForm";
import { BOOKING_COPY, fill, langOf, type PublicLang } from "./copy";
import { PublicShell, RepAvatar } from "./PublicShell";
import { SlotPicker } from "./SlotPicker";
import { browserZone, whenWithZone } from "./slotGroups";
import { type BookResult, type PublicBookingApi, PublicBookingError, type PublicRep } from "./api/types";

export const PublicBookingPage = ({ api }: { api: PublicBookingApi }) => {
  const { slug = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const lang = langOf(params.get("lang"));
  const c = BOOKING_COPY[lang];
  const tz = browserZone();
  const [rep, setRep] = useState<PublicRep | null>(null);
  const [missing, setMissing] = useState(false);
  const [slot, setSlot] = useState<string | null>(null);
  const [values, setValues] = useState<FormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [taken, setTaken] = useState<string[]>([]);
  const [pickerKey, setPickerKey] = useState(0);
  const [done, setDone] = useState<BookResult | null>(null);
  const inFlight = useRef(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const selectedRef = useRef<HTMLHeadingElement>(null);
  const doneRef = useRef<HTMLHeadingElement>(null);
  // Focus follows the content that replaces the one the visitor just used (a11y): the picked slot, the picker's
  // title after a taken slot or « Changer de moment » (final review M6), the confirmation.
  const [backToPicker, setBackToPicker] = useState(0);
  useEffect(() => {
    if (slot) selectedRef.current?.focus();
  }, [slot]);
  useEffect(() => {
    if (pickerKey > 0 || backToPicker > 0) titleRef.current?.focus();
  }, [pickerKey, backToPicker]);
  useEffect(() => {
    if (done) doneRef.current?.focus();
  }, [done]);
  const repName = rep ? `${rep.firstName} ${rep.lastName}`.trim() : "";

  const setLang = (l: PublicLang) => {
    const p = new URLSearchParams(params);
    if (l === "en") p.set("lang", "en");
    else p.delete("lang");
    setParams(p, { replace: true });
  };

  const submit = async () => {
    if (inFlight.current || !slot) return; // a double click books once
    const e = validateBookingForm(values);
    setErrors(e);
    const firstInvalid = (["name", "company", "email", "phone"] as const).find((k) => e[k]);
    if (firstInvalid) {
      document.getElementById(`bk-${firstInvalid}`)?.focus();
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      setDone(await api.book({ slug, start: slot, ...values, lang }));
    } catch (err) {
      const code = err instanceof PublicBookingError ? err.code : "error";
      if (code === "slot_taken") {
        // With or without refreshed slots, the picker reloads without the slot that was just taken
        setTaken((t) => [...t, slot]);
        setSlot(null);
        setNotice(c.taken);
        setPickerKey((k) => k + 1);
      } else if (code === "invalid_phone") {
        setErrors({ phone: "invalidPhone" });
        document.getElementById("bk-phone")?.focus();
      }
      else if (code === "not_found") setMissing(true);
      else if (code === "already_booked") setNotice(fill(c.alreadyBooked, { rep: repName || c.yourRep }));
      else if (code === "in_progress") setNotice(c.inProgress);
      else if (code === "rate_limited") setNotice(c.rateLimited);
      else if (code === "calendar_unavailable") setNotice(c.unavailable);
      else setNotice(c.error);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (missing)
    return (
      <PublicShell lang={lang} onLang={setLang}>
        <h1 className="text-2xl font-extrabold">{c.notFound}</h1>
        <p className="mt-2">{c.notFoundDetail}</p>
      </PublicShell>
    );

  if (done)
    return (
      <PublicShell lang={lang}>
        <h1 ref={doneRef} tabIndex={-1} className="text-2xl font-extrabold outline-none">
          {done.status === "received" ? c.received : c.doneTitle}
        </h1>
        {done.status !== "received" && done.startsAt && (
          <>
            <p title={tz} className="mt-2">{fill(c.doneWhen, { when: whenWithZone(done.startsAt, tz, lang) })}</p>
            <p className="mt-1 text-sm text-muted-foreground">{c.doneDetail}</p>
          </>
        )}
        {done.manageToken && (
          <a
            className="mt-4 inline-block text-accent-foreground underline underline-offset-4"
            href={`#/demo/gerer/${done.manageToken}${lang === "en" ? "?lang=en" : ""}`}
          >
            {c.manageLink}
          </a>
        )}
      </PublicShell>
    );

  return (
    <PublicShell lang={lang} onLang={setLang}>
      <div className="flex items-center gap-3">
        {rep && <RepAvatar firstName={rep.firstName} lastName={rep.lastName} avatarUrl={rep.avatarUrl} />}
        <div>
          <h1 ref={titleRef} tabIndex={-1} className="text-2xl font-extrabold outline-none">
            {c.title}
          </h1>
          {rep && <p className="text-muted-foreground">{fill(c.with, { name: repName })}</p>}
        </div>
      </div>
      {notice && (
        <p role="alert" className="mt-4 rounded-md border border-destructive/40 p-3 text-sm">
          {notice}
        </p>
      )}
      {slot ? (
        <div className="mt-4">
          <h2 ref={selectedRef} tabIndex={-1} title={tz} className="font-semibold outline-none">
            {fill(c.selected, { when: whenWithZone(slot, tz, lang) })}
          </h2>
          <Button
            variant="link"
            className="px-0"
            onClick={() => {
              setSlot(null);
              setBackToPicker((n) => n + 1);
            }}
          >
            {c.change}
          </Button>
          <BookingForm lang={lang} values={values} errors={errors} busy={busy} onChange={setValues} onSubmit={submit} />
        </div>
      ) : (
        <div className="mt-4">
          <SlotPicker
            key={pickerKey}
            api={api}
            query={{ slug }}
            lang={lang}
            exclude={taken}
            onRep={setRep}
            onError={(code) => code === "not_found" && setMissing(true)}
            onPick={(iso) => {
              setNotice(null);
              setSlot(iso);
            }}
          />
        </div>
      )}
    </PublicShell>
  );
};
